// Admin user management — list, create, change role, disable.
//
// Visible to `effective_role === 'admin'` only. Backend enforces the
// same gate (Action.ADMIN_USERS_*) so this route is cosmetic — a non-
// admin who deep-links here gets the route's "forbidden" pane plus the
// API 403 they'd hit anyway.
//
// The role <select> respects tier: on Lite, only `admin` is offered.
// The "last admin" guard is enforced by the backend (409); we surface
// the message through the standard ApiError flow.

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import {
  useAdminUsers,
  useCreateAdminUser,
  useDisableAdminUser,
  useMe,
  useUnlockAdminUser,
  useUpdateAdminUser,
} from '@/api/queries';
import type { AdminRole, AdminUser } from '@/api/types';
import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/_authed/users')({
  component: UsersPage,
});

const ALL_ROLES: AdminRole[] = ['admin', 'operator', 'accountant', 'support', 'viewer'];

function rolesAvailable(extended: boolean): AdminRole[] {
  return extended ? ALL_ROLES : ['admin'];
}

function UsersPage() {
  const { t } = useTranslation();
  const me = useMe();
  const list = useAdminUsers();

  const extended = !!me.data?.entitlements?.features?.rbac_extended_roles;
  const available = rolesAvailable(extended);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  if (me.data && me.data.effective_role !== 'admin') {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('users.title', { defaultValue: 'Users' })}
        </h1>
        <Empty
          title={t('users.forbidden', {
            defaultValue: 'Only the admin role can manage users.',
          })}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('users.title', { defaultValue: 'Users' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('users.subtitle', {
              defaultValue:
                'Manage admin staff: roles, status, and lockout. The role assignment respects the active license tier.',
            })}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          {t('users.create', { defaultValue: 'Create user' })}
        </Button>
      </header>

      {!extended && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
          {t('users.lite_notice', {
            defaultValue:
              "On the Lite tier only the 'admin' role can be assigned. Upgrade to Pro to use operator / accountant / support / viewer.",
          })}
        </div>
      )}

      {list.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('users.col_username', { defaultValue: 'Username' })}</TableHead>
              <TableHead>{t('users.col_role', { defaultValue: 'Role' })}</TableHead>
              <TableHead>{t('users.col_status', { defaultValue: 'Status' })}</TableHead>
              <TableHead>{t('users.col_mfa', { defaultValue: 'MFA' })}</TableHead>
              <TableHead>{t('users.col_last_login', { defaultValue: 'Last login' })}</TableHead>
              <TableHead className="text-end">
                {t('users.col_actions', { defaultValue: 'Actions' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.username}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-[10px] uppercase">
                    {u.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={u.status === 'active' ? 'secondary' : 'outline'}
                    className="text-[10px] uppercase"
                  >
                    {u.status}
                  </Badge>
                </TableCell>
                <TableCell>{u.mfa_enabled ? '✓' : '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell className="text-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={u.id === me.data?.id}
                    onClick={() => setEditing(u)}
                  >
                    {t('users.edit', { defaultValue: 'Edit' })}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {createOpen && (
        <CreateUserDialog
          available={available}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editing && (
        <EditUserDialog
          user={editing}
          available={available}
          isSelf={editing.id === me.data?.id}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CreateUserDialog({
  available,
  onClose,
}: {
  available: AdminRole[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateAdminUser();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AdminRole>('viewer');

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { username, password, role },
      { onSuccess: onClose },
    );
  };

  const apiError = create.error instanceof ApiError ? create.error : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('users.create', { defaultValue: 'Create user' })}</DialogTitle>
            <DialogDescription>
              {t('users.create_hint', {
                defaultValue:
                  'The user can sign in immediately. They should enable MFA on first login.',
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="u-name">{t('users.col_username', { defaultValue: 'Username' })}</Label>
            <Input
              id="u-name"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              minLength={3}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="u-pass">
              {t('users.password', { defaultValue: 'Password (min 12)' })}
            </Label>
            <Input
              id="u-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>{t('users.col_role', { defaultValue: 'Role' })}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as AdminRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {available.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {apiError && <ErrorBox message={apiError.message} />}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t('common.saving', { defaultValue: 'Saving…' })
                : t('common.save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({
  user,
  available,
  isSelf,
  onClose,
}: {
  user: AdminUser;
  available: AdminRole[];
  isSelf: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateAdminUser();
  const disable = useDisableAdminUser();
  const unlock = useUnlockAdminUser();
  const [role, setRole] = useState<AdminRole>(user.role);
  const [status, setStatus] = useState<'active' | 'disabled'>(user.status);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSelf) return;
    update.mutate(
      { id: user.id, body: { role, status } },
      { onSuccess: onClose },
    );
  };

  const onDisable = () => {
    if (isSelf) return;
    disable.mutate(user.id, { onSuccess: onClose });
  };

  const apiError =
    (update.error instanceof ApiError && update.error) ||
    (disable.error instanceof ApiError && disable.error) ||
    null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {t('users.edit_title', { defaultValue: 'Edit {{name}}', name: user.username })}
            </DialogTitle>
            {isSelf && (
              <DialogDescription className="text-warning-foreground">
                {t('users.cannot_edit_self', {
                  defaultValue:
                    "You can't change your own role or status — use the host CLI if you really need to.",
                })}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-2">
            <Label>{t('users.col_role', { defaultValue: 'Role' })}</Label>
            <Select
              value={role}
              onValueChange={(v) => setRole(v as AdminRole)}
              disabled={isSelf}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {available.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('users.col_status', { defaultValue: 'Status' })}</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as 'active' | 'disabled')}
              disabled={isSelf}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">active</SelectItem>
                <SelectItem value="disabled">disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {apiError && <ErrorBox message={apiError.message} />}

          <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => unlock.mutate(user.id)}
                disabled={unlock.isPending}
              >
                {t('users.unlock', { defaultValue: 'Clear lockout' })}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={onDisable}
                disabled={isSelf || user.status === 'disabled' || disable.isPending}
              >
                {t('users.disable', { defaultValue: 'Disable' })}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={isSelf || update.isPending}>
                {update.isPending
                  ? t('common.saving', { defaultValue: 'Saving…' })
                  : t('common.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
